import { useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { ServerControls } from '@/deploy/components/ui/ServerControls';
import type { ApiInstance, ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import type { ServerScope } from '@/deploy/services/server-control.service';
import type { ProjectGroup } from '@/deploy/types';
import { apiGroupLabel, canonicalApiEnv, versionStatus } from '@/deploy/utils/apiEnvs';
import {
  endpointOn, hasControllableServer, instanceDisplayName, instanceLabel,
  isDefaultInstance, linkOn, serverKey, servedGroupsOf, versionOn,
} from '@/deploy/utils/apiInstances';
import styles from './ApiInstances.module.scss';

// ============================================================
// Sous-lignes d'instances de la page APIs : quand plusieurs instances d'une API sont présentes
// sur l'env, la zone endpoint de la ligne en liste une par sous-ligne (drapeaux des groupes
// servis, libellé, ip:port, version, état du lien, menu de contrôles de ce serveur).
// ============================================================

/**
 * État de Release d'une instance sur la ligne : en file (deux à la fois au plus), en cours, ou
 * redémarrée et dont le CICD suit encore le démarrage (serveur toujours verrouillé).
 */
export type InstancePending = 'queued' | 'running' | 'verifying';

/**
 * Drapeaux des groupes servis puis libellé de l'instance. Le libellé est affiché hors
 * Principale, ou quand la Principale ne sert aucun groupe (sinon la sous-ligne serait anonyme).
 */
export function InstanceTag({ inst, served, forceLabel }: {
  inst: ApiInstance;
  served: ProjectGroup[];
  /** Toujours afficher le libellé (modales). */
  forceLabel?: boolean;
}) {
  const principal = isDefaultInstance(inst);
  const showLabel = forceLabel || !principal || served.length === 0;
  if (served.length === 0 && !showLabel) return null;
  return (
    <span className={styles.instTag}
      title={principal ? 'Reçoit aussi les serveurs sans RECUBE_GROUP' : undefined}>
      {served.map((g) => <GroupBadge key={g.key} group={g} />)}
      {showLabel && <span className={styles.instLabel}>{instanceLabel(inst)}</span>}
    </span>
  );
}

/** Pastille ip:port copiable. `legacy` : infobulle sur le rattachement à venir. */
export function InstanceEndpoint({ endpoint, legacy }: { endpoint: string; legacy?: boolean }) {
  return (
    <button
      type="button"
      className={styles.instEndpoint}
      onClick={() => void navigator.clipboard.writeText(endpoint)}
      title={legacy
        ? 'Serveur retrouvé par son image, il sera rattaché au prochain déploiement. Clic : copier l\'adresse (IP:port)'
        : "Copier l'adresse (IP:port)"}
    >
      <Icon name="server" size={10} /> {endpoint}
    </button>
  );
}

/**
 * État du lien d'une instance sur l'env, quand il faut le signaler : plusieurs serveurs,
 * serveur introuvable, lien cassé, ou aucun serveur alors que des groupes lui sont répartis
 * (toujours pour la Principale, qui reçoit aussi les serveurs sans groupe).
 */
export function InstanceLinkNotice({ api, inst, envId, served, onCreateServer }: {
  api: ApiService;
  inst: ApiInstance;
  envId: ApiEnvId;
  served: ProjectGroup[];
  onCreateServer?: () => void;
}) {
  const link = linkOn(inst, envId);
  const envLabel = apiGroupLabel(envId);
  if (link === 'ambiguous') {
    return (
      <span title={"Plusieurs serveurs Ptero ont l'image de cette API : rattache le bon avec « Rattacher un serveur… ». Déployer et Release sont bloqués d'ici là."}>
        <Badge tone="warning">Plusieurs serveurs</Badge>
      </span>
    );
  }
  if (link === 'missing') {
    return (
      <span title="Le serveur rattaché n'existe plus sur Ptero : Déployer en recrée un, ou rattache un autre serveur.">
        <Badge tone="danger">Serveur introuvable</Badge>
      </span>
    );
  }
  if (link === 'broken') {
    return (
      <span title="Le serveur rattaché n'a pas l'image de cette API : rattache le bon serveur (Rattacher un serveur…).">
        <Badge tone="danger">Lien cassé</Badge>
      </span>
    );
  }
  const principal = isDefaultInstance(inst);
  if (link === 'none' && (served.length > 0 || principal)) {
    const who = served.length === 0 ? 'les serveurs sans groupe'
      : `les serveurs ${served.map((g) => g.label).join(', ')}${principal ? ' et ceux sans groupe' : ''}`;
    return (
      <>
        <span className={[styles.instNote, styles.instNoteWarn].join(' ')}>
          <Icon name="warn" size={10} /> Pas de serveur sur {envLabel} : {who} ne reçoivent pas {api.name}
        </span>
        {onCreateServer && (
          <Button variant="softPrimary" size="sm" leftIcon={<Icon name="plus" size={11} />} onClick={onCreateServer}>
            Créer le serveur…
          </Button>
        )}
      </>
    );
  }
  return null;
}

/** Disclosure « Actions » d'une instance sans serveur pilotable (pas de power ni de console). */
function InstanceActionsMenu({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeOnEscape = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Escape' || !open) return;
    e.stopPropagation();
    setOpen(false);
    toggleRef.current?.focus();
  };
  return (
    <>
      <Button ref={toggleRef} variant="ghost" size="sm"
        rightIcon={<Icon name="chevron-down" size={11}
          style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 120ms' }} />}
        aria-expanded={open} aria-controls={id} aria-label={`Actions de ${label}`}
        onClick={() => setOpen((o) => !o)} onKeyDown={closeOnEscape}>
        Actions
      </Button>
      {open && (
        <div id={id} role="group" aria-label={`Actions de ${label}`} onKeyDown={closeOnEscape}
          className={[styles.instFull, styles.instMenu].join(' ')}>
          {children}
        </div>
      )}
    </>
  );
}

export interface ApiInstanceLinesProps {
  api: ApiService;
  projectId: string;
  envId: ApiEnvId;
  /** Instances présentes sur l'env (instancesOnEnv), Principale en tête. */
  instances: ApiInstance[];
  groups: ProjectGroup[];
  pendingOf: (inst: ApiInstance) => InstancePending | undefined;
  /** Actions ajoutées au menu de la sous-ligne (Release de l'instance, Fichiers, Rattacher...). */
  renderMenu: (inst: ApiInstance) => ReactNode;
  /** Ligne de résultat de la dernière Release de l'instance sur l'env, ou null. */
  renderResult: (inst: ApiInstance) => ReactNode;
  onCreateServer: (inst: ApiInstance) => void;
}

export function ApiInstanceLines({
  api, projectId, envId, instances, groups, pendingOf, renderMenu, renderResult, onCreateServer,
}: ApiInstanceLinesProps) {
  const dataEnv = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(envId);
  return (
    <div className={styles.instLines}>
      {instances.map((inst) => {
        const served = servedGroupsOf(api, inst, dataEnv, groups);
        const version = versionOn(inst, dataEnv);
        const endpoint = endpointOn(inst, dataEnv);
        const link = linkOn(inst, dataEnv);
        const controllable = hasControllableServer(inst, dataEnv);
        // Flèche aussi pour une instance ambiguous en retard : lue, mais bloquée pour la Release
        const behind = controllable && versionStatus(version, api.latestAvailable) === 'behind';
        const pending = pendingOf(inst);
        const name = instanceDisplayName(api, inst);
        const result = renderResult(inst);
        return (
          <div key={inst.key} className={styles.instLine}>
            <InstanceTag inst={inst} served={served} />
            {endpoint && <InstanceEndpoint endpoint={endpoint} legacy={link === 'legacy'} />}
            {version && <Badge tone="info">v{version}</Badge>}
            {behind && <Badge tone="warning"><Icon name="arrow-right" size={9} /> v{api.latestAvailable}</Badge>}
            <InstanceLinkNotice api={api} inst={inst} envId={dataEnv} served={served}
              onCreateServer={() => onCreateServer(inst)} />
            {groups.length > 0 && served.length === 0 && controllable && (
              <span className={styles.instNote}>
                {/* La Principale garde les serveurs sans RECUBE_GROUP, même si chaque groupe est ailleurs ou sur « Aucune » */}
                {isDefaultInstance(inst) ? `ne sert que les serveurs sans groupe sur ${envLabel}` : `ne sert aucun groupe sur ${envLabel}`}
              </span>
            )}
            {pending === 'queued' && <span className={styles.instNote}>Release en attente…</span>}
            <span className={styles.instSpacer} />
            {controllable ? (
              <ServerControls
                projectId={projectId}
                scope={`api-${dataEnv}` as ServerScope}
                targetKey={serverKey(api, inst)}
                sourceName={name}
                sourceSubtitle={`${envLabel}${version ? ` · v${version}` : ''}`}
                icon="zap"
                disabled={pending !== undefined}
                layout="menu"
                menuExtra={renderMenu(inst)}
              />
            ) : (
              <InstanceActionsMenu label={name}>{renderMenu(inst)}</InstanceActionsMenu>
            )}
            {result && <div className={styles.instFull}>{result}</div>}
          </div>
        );
      })}
    </div>
  );
}
